import * as React from 'react';
import RequestHotfixModal from './hotfixRequests/RequestHotfixModal';
import { selectActiveHotfixRequestsQuery, selectCanCreateHotfix, selectCanRequestHotfix } from '@/selectors/selectors';
import { connect, ConnectedProps } from 'react-redux';
import { RootState } from '../reducers/reducers';
import { FieldBinaryOutlined, CaretDownOutlined, NodeExpandOutlined, FireOutlined } from '@ant-design/icons';
import CreateEditReleaseModal from './CreateEditReleaseModal';
import { ReleaseStatus, ReleaseTimelinePhase } from '../types';
import { NewReleasePayloadType, tryNewReleaseAction } from '../actions/release';
import { AppComponentProps } from '../typescriptHelpers';
import ReleaseGroup from './ReleaseGroup';
import Dropdown from 'antd/lib/dropdown';
import Text from 'antd/lib/typography/Text';
import Spin from 'antd/lib/spin';
import ConfigProvider from 'antd/lib/config-provider';
import antdTheme from 'antd/lib/theme';
import {
    selectOrg,
    selectApp,
    selectPaginatedReleasesLimitAndOffset,
    selectIsAppsDemoModeEnabled,
    selectIsWorkflowsSSRStepsEnabled,
} from '../selectors/selectors';
import { ReleasesRequest, tryFetchPaginatedEntities } from '../actions/entities';
import { FetchableEntityType, FetchablePaginatedEntityType } from '../entityType';
import PrepareHotfixOrRollbackReleaseModal from './rollbacks/PrepareHotfixOrRollbackReleaseModal';
import { PrepareNewWorkflowRunModal } from './prepareRelease/PrepareNewWorkflowRunModal';
import { selectCompletedReleases, selectCurrentReleases, selectUpcomingReleases } from '@/selectors/memoizedSelectors';
import { getIsLoading } from '../helpers';
import { getAntdThemeTokens, runwayColors, getNavDropdownMenuStyle } from './styles/globalStyles';
import { SidebarColors, useThemeMode } from './styles/themeMode';
import HotfixRequestsSidebarGroup from './hotfixRequests/HotfixRequestsSidebarGroup';
import { Link } from 'react-router-dom';
import { createHotfixRequestRoute } from '@/helpers/routeHelpers';

enum PrepareReleaseType {
    Standard = 'release',
    Hotfix = 'hotfix',
    RequestHotfix = 'requestHotfix',
}

const MainPageTheme: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const theme = useThemeMode().mode === 'dark' ? 'dark' : 'light';
    return (
        <ConfigProvider
            theme={{
                algorithm: theme === 'dark' ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm,
                token: getAntdThemeTokens(theme),
            }}
        >
            {children}
        </ConfigProvider>
    );
};

interface State {
    createEditReleaseModalVisible: boolean;
    cretateEditHotfixModalVisible: boolean;
    requestHotfixModalVisible: boolean;
    isDropdownOpen: boolean;
}

class Releases extends React.Component<Props, State> {
    constructor(props) {
        super(props);

        this.state = {
            createEditReleaseModalVisible: false,
            cretateEditHotfixModalVisible: false,
            requestHotfixModalVisible: false,
            isDropdownOpen: false,
        };
    }

    componentDidMount(): void {
        if (this.props.shouldFetchCompletedReleases && !this.props.hasCompletedReleases) {
            this.fetchCompletedReleases();
        }
    }

    componentDidUpdate(prevProps: Props) {
        const shouldFetchBecameTrue =
            !prevProps.shouldFetchCompletedReleases && this.props.shouldFetchCompletedReleases;

        if (shouldFetchBecameTrue && !this.props.hasCompletedReleases) {
            this.fetchCompletedReleases();
        }
    }

    fetchCompletedReleases = () => {
        if (!this.props.shouldFetchCompletedReleases) {
            return;
        }

        const limit = this.props.releasesPaginationLimit ?? 10;
        const offset = this.props.releasePaginationOffset ?? 0;

        this.props.fetchReleases(
            {
                orgId: this.props.orgId,
                appId: this.props.appId,
                orgIdParam: this.props.orgIdParam,
                appIdParam: this.props.appIdParam,
            },
            limit,
            offset,
        );
    };

    handleMenuClick = (e) => {
        if (e.key == PrepareReleaseType.Hotfix) {
            if (!this.props.canCreateHotfix) return;
            this.setState({
                ...this.state,
                cretateEditHotfixModalVisible: true,
            });
        } else if (e.key === PrepareReleaseType.RequestHotfix) {
            if (!this.props.canRequestHotfix || this.props.hasActiveHotfixRequest) return;
            this.setState({
                ...this.state,
                requestHotfixModalVisible: true,
            });
        } else if (e.key === PrepareReleaseType.Standard) {
            this.setState({
                ...this.state,
                createEditReleaseModalVisible: true,
            });
        }
    };

    get menuItems() {
        const sc = this.props.sidebarColors;
        return [
            {
                key: PrepareReleaseType.Standard,
                label: (
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8,
                            padding: '4px 0px',
                            borderRadius: 'inherit',
                        }}
                    >
                        <NodeExpandOutlined style={{ color: runwayColors.primary, fontSize: 16 }} />
                        <Text style={{ color: sc.text, fontSize: 14 }}>Release</Text>
                    </div>
                ),
            },
            ...(this.props.canCreateHotfix ?
                [
                    {
                        key: PrepareReleaseType.Hotfix,
                        label: (
                            <div
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 8,
                                    padding: '4px 0px',
                                    borderRadius: 'inherit',
                                }}
                            >
                                <FireOutlined style={{ color: runwayColors.statusYellow, fontSize: 16 }} />
                                <Text style={{ color: sc.text, fontSize: 14 }}>Hotfix</Text>
                            </div>
                        ),
                    },
                ]
            :   []),
            ...(this.props.canRequestHotfix ?
                [
                    {
                        key: PrepareReleaseType.RequestHotfix,
                        label:
                            this.props.hasActiveHotfixRequest && this.props.activeHotfixRequestId ?
                                <Link
                                    to={createHotfixRequestRoute(
                                        this.props.orgId,
                                        this.props.appId,
                                        this.props.activeHotfixRequestId,
                                    )}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 8,
                                        padding: '4px 0px',
                                        borderRadius: 'inherit',
                                    }}
                                >
                                    <FireOutlined style={{ color: runwayColors.statusYellow, fontSize: 16 }} />
                                    <Text style={{ color: sc.text, fontSize: 14 }}>Pending hotfix request...</Text>
                                </Link>
                            :   <div
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 8,
                                        padding: '4px 0px',
                                        borderRadius: 'inherit',
                                    }}
                                >
                                    <FireOutlined style={{ color: runwayColors.statusYellow, fontSize: 16 }} />
                                    <Text style={{ color: sc.text, fontSize: 14 }}>Hotfix request</Text>
                                </div>,
                    },
                ]
            :   []),
        ];
    }

    releaseGroups = () => {
        const hasNoReleases =
            !this.props.hasUpcomingReleases && !this.props.hasCurrentReleases && !this.props.hasCompletedReleases;

        if (hasNoReleases && this.props.isLoadingReleases) {
            return (
                <div style={{ minHeight: '40vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <Spin style={{ fontSize: 15 }} />
                </div>
            );
        }

        return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                {this.props.hasUpcomingReleases && (
                    <ReleaseGroup
                        orgId={this.props.orgId}
                        appId={this.props.appId}
                        groupPhase={ReleaseTimelinePhase.Upcoming}
                        orgIdParam={this.props.orgIdParam}
                        appIdParam={this.props.appIdParam}
                    />
                )}
                {this.props.hasCurrentReleases && (
                    <ReleaseGroup
                        orgId={this.props.orgId}
                        appId={this.props.appId}
                        groupPhase={ReleaseTimelinePhase.Current}
                        orgIdParam={this.props.orgIdParam}
                        appIdParam={this.props.appIdParam}
                    />
                )}
                <HotfixRequestsSidebarGroup
                    orgId={this.props.orgId}
                    appId={this.props.appId}
                    sidebarColors={this.props.sidebarColors}
                />
                {this.props.hasCompletedReleases && (
                    <ReleaseGroup
                        orgId={this.props.orgId}
                        appId={this.props.appId}
                        groupPhase={ReleaseTimelinePhase.Completed}
                        orgIdParam={this.props.orgIdParam}
                        appIdParam={this.props.appIdParam}
                        onClickLoadMore={this.fetchCompletedReleases}
                        totalCount={this.props.completedReleaseTotalCount}
                        canLoadMore={this.props.shouldFetchCompletedReleases}
                    />
                )}
                {!this.props.hasCompletedReleases && this.props.isLoadingReleases && (
                    <div style={{ height: 50, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                        <Spin style={{ fontSize: 15 }} />
                    </div>
                )}
            </div>
        );
    };

    renderModals() {
        const requestModal = (
            <RequestHotfixModal
                orgId={this.props.orgId}
                appId={this.props.appId}
                open={this.state.requestHotfixModalVisible}
                onCancel={() => this.setState({ requestHotfixModalVisible: false })}
            />
        );
        if (this.props.isSSRMigrated) {
            return (
                <MainPageTheme>
                    {requestModal}
                    <PrepareNewWorkflowRunModal
                        orgId={this.props.orgId}
                        appId={this.props.appId}
                        open={this.state.createEditReleaseModalVisible}
                        mode="standard"
                        onCancel={() => {
                            this.setState({
                                ...this.state,
                                createEditReleaseModalVisible: false,
                            });
                        }}
                        onSuccess={() => {
                            this.setState({
                                ...this.state,
                                createEditReleaseModalVisible: false,
                            });
                        }}
                    />
                    <PrepareNewWorkflowRunModal
                        orgId={this.props.orgId}
                        appId={this.props.appId}
                        open={this.state.cretateEditHotfixModalVisible && this.props.canCreateHotfix}
                        mode="hotfix"
                        isConvertingRollback={this.props.lastCompletedReleaseHasActiveRollback}
                        onCancel={() => {
                            this.setState({
                                ...this.state,
                                cretateEditHotfixModalVisible: false,
                            });
                        }}
                        onSuccess={() => {
                            this.setState({
                                ...this.state,
                                cretateEditHotfixModalVisible: false,
                            });
                        }}
                    />
                </MainPageTheme>
            );
        }

        return (
            <MainPageTheme>
                {requestModal}
                <CreateEditReleaseModal
                    orgId={this.props.orgId}
                    appId={this.props.appId}
                    open={this.state.createEditReleaseModalVisible}
                    onCancel={() => {
                        this.setState({
                            ...this.state,
                            createEditReleaseModalVisible: false,
                        });
                    }}
                    onDidCreateOrEditRelease={() => {
                        this.setState({
                            ...this.state,
                            createEditReleaseModalVisible: false,
                        });
                    }}
                    isEditingRelease={false}
                    displayOnlyTargetDates={false}
                    canBeSubmitted={null}
                />
                <PrepareHotfixOrRollbackReleaseModal
                    orgId={this.props.orgId}
                    appId={this.props.appId}
                    open={this.state.cretateEditHotfixModalVisible && this.props.canCreateHotfix}
                    isConvertingRollback={this.props.lastCompletedReleaseHasActiveRollback}
                    onCancel={() => {
                        this.setState({
                            ...this.state,
                            cretateEditHotfixModalVisible: false,
                        });
                    }}
                />
            </MainPageTheme>
        );
    }

    render() {
        const sc = this.props.sidebarColors;
        return (
            <div>
                {this.renderModals()}
                <div style={{ padding: '20px 16px 10px 16px' }}>
                    <Dropdown
                        menu={{
                            items: this.menuItems,
                            onClick: this.handleMenuClick,
                            style: getNavDropdownMenuStyle(sc),
                        }}
                        overlayClassName={
                            this.props.sidebarTheme === 'light' ? 'light-sidebar-dropdown-menu' : 'dark-dropdown-menu'
                        }
                        trigger={['click']}
                        onOpenChange={(open) => this.setState({ isDropdownOpen: open })}
                    >
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                cursor: 'pointer',
                                padding: '5px 12px',
                                border: `1px solid ${sc.border}`,
                                borderRadius: 4,
                                backgroundColor:
                                    this.state.isDropdownOpen ? sc.dropdownBg
                                    : this.props.sidebarTheme === 'light' ? sc.dropdownBg
                                    : 'transparent',
                            }}
                        >
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <FieldBinaryOutlined style={{ color: sc.icon, fontSize: 16 }} />
                                <Text style={{ color: sc.text, fontSize: 14 }}>Prepare new...</Text>
                            </div>
                            <CaretDownOutlined style={{ color: sc.textMuted, fontSize: 16, marginLeft: 12 }} />
                        </div>
                    </Dropdown>
                </div>
                <div>{this.releaseGroups()}</div>
            </div>
        );
    }
}

type ReleasesProps = AppComponentProps & { sidebarColors: SidebarColors; sidebarTheme: 'light' | 'dark' };

const mapProps = (state: RootState, ownProps: ReleasesProps) => {
    const { orgId, appId } = ownProps;

    const org = selectOrg(state, orgId);
    const app = selectApp(state, appId);
    const completedReleases = selectCompletedReleases(state, appId);
    const currentReleases = selectCurrentReleases(state, appId);
    const upcomingReleases = selectUpcomingReleases(state, appId);
    const isDemoModeEnabled = selectIsAppsDemoModeEnabled(state);

    const paginatedReleasesPageLimitAndOffset = selectPaginatedReleasesLimitAndOffset(state, appId);

    const releasesPaginationLimit = paginatedReleasesPageLimitAndOffset?.limit;
    const releasePaginationOffset = paginatedReleasesPageLimitAndOffset?.offset;
    const paginatedReleasesTotalCount = paginatedReleasesPageLimitAndOffset?.totalCount;
    const completedReleaseTotalCount = paginatedReleasesTotalCount ?? app?.completedReleaseTotalCount ?? 0;
    const shouldFetchCompletedReleases =
        (releasePaginationOffset ?? 0) < completedReleaseTotalCount && !isDemoModeEnabled;

    const lastCompletedReleaseHasActiveRollback =
        completedReleases.length > 0 ? completedReleases[0].rollbackInfo?.status === ReleaseStatus.Active : false;

    const isSSRMigrated = selectIsWorkflowsSSRStepsEnabled(state, appId);
    const isLoadingAppState = getIsLoading(FetchableEntityType.AppState, state, appId);
    const isLoadingCompletedReleases = getIsLoading(FetchablePaginatedEntityType.Releases, state, appId);
    const isLoadingReleases = isLoadingAppState || isLoadingCompletedReleases;

    const activeHotfixRequestsQuery = selectActiveHotfixRequestsQuery({ orgId, appId })(state);
    const activeHotfixRequest = activeHotfixRequestsQuery.data?.[0];

    return {
        canCreateHotfix: selectCanCreateHotfix(state, { orgId, appId }),
        canRequestHotfix: selectCanRequestHotfix(state, { orgId, appId }),
        hasActiveHotfixRequest: !!activeHotfixRequest,
        activeHotfixRequestId: activeHotfixRequest?.id,
        appId,
        orgId,
        orgIdParam: org?.orgId ?? '',
        appIdParam: app?.appId ?? '',
        releasesPaginationLimit,
        releasePaginationOffset,
        shouldFetchCompletedReleases,
        completedReleaseTotalCount,
        lastCompletedReleaseHasActiveRollback,
        isSSRMigrated,
        isLoadingReleases,
        hasUpcomingReleases: upcomingReleases.length > 0,
        hasCurrentReleases: currentReleases.length > 0,
        hasCompletedReleases: completedReleases.length > 0,
    };
};

const mapDispatch = (dispatch) => {
    return {
        createNewRelease: (payload: NewReleasePayloadType) => {
            dispatch(tryNewReleaseAction(payload));
        },
        fetchReleases: (request: ReleasesRequest, limit: number, offset: number) => {
            dispatch(
                tryFetchPaginatedEntities(FetchablePaginatedEntityType.Releases, request, limit, offset, request.appId),
            );
        },
    };
};

const connector = connect(mapProps, mapDispatch);

type Props = ConnectedProps<typeof connector> & ReleasesProps;

export default connector(Releases);
